import { useState, useEffect } from "react";
import api from "@/lib/api";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  RefreshCw,
  KanbanSquare,
  GripVertical,
  MapPin,
  DollarSign,
  Shield,
  ExternalLink,
  RotateCcw,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import LeadDetailSheet from "@/components/LeadDetailSheet";

const PIPELINE_STAGES = [
  { id: "new", label: "New", color: "#2a9d8f", borderColor: "rgba(42, 157, 143, 0.5)", hint: "< 72 hours" },
  { id: "contacted", label: "Contacted", color: "#e9c46a", borderColor: "rgba(233, 196, 106, 0.5)" },
  { id: "call_set", label: "Call Set", color: "#f4a261", borderColor: "rgba(244, 162, 97, 0.5)" },
  { id: "submitted", label: "Submitted", color: "#40c9ba", borderColor: "rgba(64, 201, 186, 0.5)" },
  { id: "interview", label: "Interview", color: "#52b788", borderColor: "rgba(82, 183, 136, 0.5)" },
  { id: "offer", label: "Offer", color: "#95d5b2", borderColor: "rgba(149, 213, 178, 0.5)" },
  { id: "stale", label: "Stale", color: "#e76f51", borderColor: "rgba(231, 111, 81, 0.5)", hint: "72h - 7 days" },
  { id: "archived", label: "Archived", color: "#6c757d", borderColor: "rgba(108, 117, 125, 0.5)", hint: "> 7 days" },
];

export default function Pipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leads');
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to load pipeline");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const moveLeadToStage = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}/status?status=${newStatus}`);
      toast.success(`Lead moved to ${newStatus.replace('_', ' ')}`);
      return true;
    } catch (error) {
      toast.error("Failed to update lead status");
      console.error(error);
      return false;
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (result) => {
    setIsDragging(false);
    
    const { destination, source, draggableId } = result;

    // If dropped outside a droppable area
    if (!destination) return;

    // If dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId;
    const leadId = draggableId;

    // Optimistically update the UI
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      )
    );

    // Make the API call
    const success = await moveLeadToStage(leadId, newStatus);

    // If the API call failed, revert the change
    if (!success) {
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, status: source.droppableId } : lead
        )
      );
    }
  };

  const refreshLead = async (leadId) => {
    try {
      await api.post(`/leads/${leadId}/refresh`);
      toast.success("Lead timer reset - moved back to New");
      // Update the lead in state to show it as New
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, status: "new" } : lead
        )
      );
      // Also update the status
      await api.put(`/leads/${leadId}/status?status=new`);
    } catch (error) {
      toast.error("Failed to refresh lead");
      console.error(error);
    }
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter((lead) => lead.status === stageId);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 50) return "text-theme-secondary";
    return "text-theme-danger";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-emerald-400/20";
    if (score >= 50) return "bg-theme-secondary/20";
    return "bg-theme-danger/20";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pipeline-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-theme-primary mx-auto mb-4" />
          <p className="text-theme-secondary font-mono">LOADING PIPELINE...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4">
          <div
            className="w-10 h-10 sm:w-14 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center flex-shrink-0"
            style={{ boxShadow: "2px 2px 0 rgba(26, 107, 98, 0.4)" }}
          >
            <KanbanSquare className="w-5 h-5 sm:w-7 sm:h-7 text-theme-secondary" />
          </div>
          <div>
            <h1
              className="text-xl sm:text-3xl font-bold text-theme-light"
              style={{ textShadow: "2px 2px 4px rgba(0,0,0,0.3)" }}
            >
              Pipeline
            </h1>
            <p className="text-theme-muted text-xs sm:text-sm font-mono hidden sm:block">
              DRAG AND DROP TO UPDATE STATUS
            </p>
          </div>
        </div>
        <Button
          className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-theme-light border border-[#40c9ba]/50 font-bold text-xs sm:text-sm px-2 sm:px-4"
          style={{ boxShadow: "2px 2px 0 rgba(42, 157, 143, 0.4)" }}
          onClick={fetchLeads}
          data-testid="refresh-pipeline-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
          <span className="hidden sm:inline">REFRESH</span>
        </Button>
      </div>

      {/* Kanban Board with Drag and Drop */}
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div
          className="flex gap-2 sm:gap-4 pb-4 sm:pb-6 overflow-x-auto kanban-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0"
          data-testid="kanban-board"
        >
          {PIPELINE_STAGES.map((stage) => {
            const stageLeads = getLeadsByStage(stage.id);
            return (
              <div
                key={stage.id}
                className="flex-shrink-0 w-[260px] sm:w-[300px]"
                data-testid={`pipeline-column-${stage.id}`}
              >
                <div
                  className="rounded-xl overflow-hidden h-full"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(26, 58, 66, 0.8), rgba(38, 70, 83, 0.9))",
                    border: `3px solid ${stage.borderColor}`,
                    boxShadow: "4px 4px 0 rgba(38, 70, 83, 0.4)",
                  }}
                >
                  {/* Column Header */}
                  <div
                    className="p-3 sm:p-4 border-b-2"
                    style={{
                      borderColor: stage.borderColor,
                      background: "rgba(26, 58, 66, 0.5)",
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: stage.color,
                            boxShadow: `0 0 10px ${stage.color}`,
                          }}
                        />
                        <div>
                          <span className="text-theme-light font-bold font-mono text-xs sm:text-sm">
                            {stage.label.toUpperCase()}
                          </span>
                          {stage.hint && (
                            <div className="flex items-center gap-1 text-[10px] text-theme-muted/70 font-mono">
                              <Clock className="w-2.5 h-2.5" />
                              {stage.hint}
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge
                        className="text-xs font-mono"
                        style={{
                          background: `${stage.color}20`,
                          color: stage.color,
                          border: `1px solid ${stage.color}40`,
                        }}
                      >
                        {stageLeads.length}
                      </Badge>
                    </div>
                  </div>

                  {/* Droppable Area */}
                  <Droppable droppableId={stage.id}>
                    {(provided, snapshot) => (
                      <ScrollArea className="h-[calc(100vh-320px)]">
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`p-3 space-y-3 min-h-[200px] transition-colors duration-200 ${
                            snapshot.isDraggingOver
                              ? "bg-theme-primary/10"
                              : ""
                          }`}
                          data-testid={`drop-zone-${stage.id}`}
                        >
                          {stageLeads.length === 0 && !snapshot.isDraggingOver ? (
                            <div className="p-6 text-center text-theme-muted/50 text-sm font-mono border-2 border-dashed border-[#a8c8c4]/20 rounded-lg">
                              DROP LEADS HERE
                            </div>
                          ) : (
                            stageLeads.map((lead, index) => (
                              <Draggable
                                key={lead.id}
                                draggableId={lead.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`p-4 rounded-lg cursor-grab transition-all ${
                                      snapshot.isDragging
                                        ? "shadow-2xl rotate-2 scale-105"
                                        : "hover:translate-y-[-2px]"
                                    }`}
                                    style={{
                                      background: snapshot.isDragging
                                        ? "linear-gradient(145deg, rgba(42, 157, 143, 0.9), rgba(26, 58, 66, 0.95))"
                                        : "linear-gradient(145deg, rgba(38, 70, 83, 0.8), rgba(26, 58, 66, 0.9))",
                                      border: snapshot.isDragging
                                        ? "2px solid rgba(64, 201, 186, 0.8)"
                                        : "2px solid rgba(42, 157, 143, 0.3)",
                                      boxShadow: snapshot.isDragging
                                        ? "0 20px 40px rgba(0,0,0,0.4)"
                                        : "3px 3px 0 rgba(38, 70, 83, 0.4)",
                                      ...provided.draggableProps.style,
                                    }}
                                    data-testid={`pipeline-card-${lead.id}`}
                                  >
                                    {/* Drag Handle */}
                                    <div
                                      {...provided.dragHandleProps}
                                      className="flex items-center justify-between mb-2"
                                    >
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <GripVertical className="w-4 h-4 text-theme-muted/50 flex-shrink-0" />
                                        <h3 className="text-sm font-semibold text-theme-light truncate">
                                          {lead.title}
                                        </h3>
                                      </div>
                                      <span
                                        className={`font-mono text-sm font-bold px-2 py-0.5 rounded ${getScoreColor(
                                          lead.score
                                        )} ${getScoreBg(lead.score)}`}
                                        style={{
                                          textShadow: "0 0 8px currentColor",
                                        }}
                                      >
                                        {lead.score}
                                      </span>
                                    </div>

                                    {/* Client */}
                                    <p className="text-xs text-theme-muted mb-3 truncate">
                                      {lead.client}
                                    </p>

                                    {/* Details Row */}
                                    <div className="flex items-center gap-2 flex-wrap mb-2">
                                      {lead.location && (
                                        <div className="flex items-center gap-1 text-xs text-theme-muted">
                                          <MapPin className="w-3 h-3" />
                                          <span className="truncate max-w-[100px]">
                                            {lead.location}
                                          </span>
                                        </div>
                                      )}
                                      {lead.rate && (
                                        <div className="flex items-center gap-1 text-xs text-theme-secondary">
                                          <DollarSign className="w-3 h-3" />
                                          <span>{lead.rate}</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Clearance Badge */}
                                    {lead.clearance && (
                                      <div className="mb-2">
                                        <Badge
                                          variant="outline"
                                          className="text-xs border-theme-accent/40 text-theme-accent bg-theme-accent/10 font-mono"
                                        >
                                          <Shield className="w-3 h-3 mr-1" />
                                          {lead.clearance}
                                        </Badge>
                                      </div>
                                    )}

                                    {/* Tech Stack */}
                                    {lead.tech_stack?.length > 0 && (
                                      <div className="flex gap-1 flex-wrap mb-3">
                                        {lead.tech_stack.slice(0, 3).map((tech) => (
                                          <span
                                            key={tech}
                                            className="px-2 py-0.5 text-xs bg-theme-dark text-theme-primary-light rounded font-mono border border-theme-primary/30"
                                          >
                                            {tech}
                                          </span>
                                        ))}
                                        {lead.tech_stack.length > 3 && (
                                          <span className="text-xs text-theme-muted font-mono">
                                            +{lead.tech_stack.length - 3}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-2 pt-2 border-t border-theme-primary/30">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 px-2 text-xs text-theme-muted hover:text-theme-light hover:bg-theme-primary/20 font-mono flex-1"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedLead(lead);
                                        }}
                                        data-testid={`view-lead-${lead.id}-btn`}
                                      >
                                        View
                                      </Button>
                                      {(lead.status === "stale" || lead.status === "archived") && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs text-theme-secondary hover:text-theme-light hover:bg-theme-secondary/20"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            refreshLead(lead.id);
                                          }}
                                          title="Reset timer & move to New"
                                          data-testid={`refresh-lead-${lead.id}-btn`}
                                        >
                                          <RotateCcw className="w-3 h-3" />
                                        </Button>
                                      )}
                                      {lead.link && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 px-2 text-xs text-theme-primary-light hover:text-theme-light hover:bg-theme-primary/20"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(lead.link, "_blank");
                                          }}
                                          data-testid={`open-link-${lead.id}-btn`}
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))
                          )}
                          {provided.placeholder}
                        </div>
                      </ScrollArea>
                    )}
                  </Droppable>
                </div>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Drag Indicator Overlay */}
      {isDragging && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-theme-dark border-2 border-theme-primary rounded-full px-6 py-3 shadow-xl z-50">
          <p className="text-theme-secondary font-mono text-sm">
            Drop on a column to change status
          </p>
        </div>
      )}

      {/* Lead Detail Sheet */}
      <LeadDetailSheet
        lead={selectedLead}
        open={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        onUpdate={fetchLeads}
      />
    </div>
  );
}
